import { useCallback, useEffect, useState } from 'react'
import { Copy, KeyRound, Play, Plus, Trash2 } from 'lucide-react'
import ErrorBanner from '@/components/ErrorBanner'
import {
  useApiKeys,
  useConsumers,
  useCreateApiKey,
  useCreateConsumer,
  useDeleteApiKey,
  useDeleteConsumer,
  useRevokeApiKey,
} from '@/hooks/useConsumers'
import { formatTime } from '@/lib/utils'
import { cn } from '@/lib/utils'
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Input,
  Table,
} from '@/admin-design/_common'
import type { ApiKey, Consumer, CreateApiKeyResponse } from '@/api/types'

function ConsumerListItem({
  c,
  active,
  onClick,
}: {
  c: Consumer
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full text-left px-3 py-2 border-b border-border last:border-0 hover:bg-muted/50',
        active && 'bg-info-soft hover:bg-info-soft',
      )}
    >
      <div className="font-mono text-sm text-foreground">{c.slug}</div>
      <div className="text-xs text-muted-foreground">{c.display_name}</div>
    </button>
  )
}

function NewConsumerForm({ onClose }: { onClose: () => void }) {
  const [slug, setSlug] = useState('')
  const [name, setName] = useState('')
  const create = useCreateConsumer()

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!slug.trim() || !name.trim()) return
    create.mutate(
      { slug: slug.trim(), display_name: name.trim() },
      {
        onSuccess: () => {
          setSlug('')
          setName('')
          onClose()
        },
      },
    )
  }

  return (
    <form onSubmit={submit} className="p-3 space-y-2 border-t border-border bg-muted/50">
      <Input
        type="text"
        placeholder="slug (lowercase, hyphens)"
        className="font-mono"
        value={slug}
        onChange={(e) => setSlug(e.target.value)}
        autoFocus
      />
      <Input
        type="text"
        placeholder="Display name"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      {create.error && <ErrorBanner error={create.error} />}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" size="sm" loading={create.isPending}>
          Create
        </Button>
      </div>
    </form>
  )
}

interface NewKeyFlashProps {
  result: CreateApiKeyResponse
  onClose: () => void
}

function NewKeyFlash({ result, onClose }: NewKeyFlashProps) {
  const [copied, setCopied] = useState(false)
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(result.raw)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // No-op — user can still select+copy manually.
    }
  }
  return (
    <div className="border border-success-soft bg-success-soft rounded p-3 space-y-2">
      <div className="text-sm font-medium text-success-soft-foreground">
        New API key — copy it now, it will not be shown again.
      </div>
      <div className="flex items-center gap-2">
        <code className="font-mono text-xs bg-card text-foreground border border-border rounded px-2 py-1 flex-1 break-all">
          {result.raw}
        </code>
        <Button type="button" variant="primary" size="sm" onClick={onCopy}>
          <Copy size={12} /> {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <div className="text-right">
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          Dismiss
        </button>
      </div>
    </div>
  )
}

function ApiKeyRow({
  k,
  onRevoke,
  onDelete,
}: {
  k: ApiKey
  onRevoke: (id: number, preview: string) => void
  onDelete: (id: number) => void
}) {
  const revoked = k.revoked_at > 0
  return (
    <Table.Row className={cn(revoked && 'opacity-50')}>
      <Table.Cell className="font-mono text-xs">{k.preview}…</Table.Cell>
      <Table.Cell className="text-xs text-muted-foreground tabular-nums">
        {k.rate_limit_per_min}/min
      </Table.Cell>
      <Table.Cell className="text-xs text-muted-foreground tabular-nums">
        {k.usage_count.toLocaleString('ko-KR')}
      </Table.Cell>
      <Table.Cell className="text-xs text-muted-foreground">
        {k.last_used_at ? formatTime(k.last_used_at) : '-'}
      </Table.Cell>
      <Table.Cell className="text-xs text-muted-foreground">
        {formatTime(k.created_at)}
      </Table.Cell>
      <Table.Cell className="text-right">
        {revoked ? (
          <div className="flex items-center justify-end gap-2">
            <Badge variant="muted">revoked</Badge>
            <button
              type="button"
              onClick={() => onDelete(k.id)}
              className="text-xs text-destructive hover:text-destructive/80 hover:underline"
            >
              Delete
            </button>
          </div>
        ) : (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => onRevoke(k.id, k.preview)}
            className="text-destructive-soft-foreground hover:bg-destructive-soft"
          >
            <Trash2 size={12} /> Revoke
          </Button>
        )}
      </Table.Cell>
    </Table.Row>
  )
}

// ─── API Playground ─────────────────────────────────────────────────────

const ENDPOINTS = [
  { label: '/api/topics', value: '/api/topics' },
  { label: '/api/topics/{id}', value: '/api/topics/{id}' },
  { label: '/api/trends', value: '/api/trends' },
]

const PARAM_HINTS: Record<string, string[]> = {
  '/api/topics': ['limit', 'since', 'source', 'tag', 'min_score', 'include_items', 'spike', 'cursor'],
  '/api/topics/{id}': ['id'],
  '/api/trends': ['limit', 'since', 'source', 'tag', 'kind', 'region', 'captured_before', 'cursor'],
}

function ApiPlayground() {
  const [endpoint, setEndpoint] = useState('/api/topics')
  const [apiKey, setApiKey] = useState('')
  const [params, setParams] = useState<Record<string, string>>({})
  const [result, setResult] = useState<{ status: number; body: string; elapsed: number } | null>(null)
  const [loading, setLoading] = useState(false)

  const hints = PARAM_HINTS[endpoint] ?? []

  const setParam = (key: string, value: string) => {
    setParams((prev) => {
      const next = { ...prev }
      if (value) next[key] = value
      else delete next[key]
      return next
    })
  }

  const buildUrl = useCallback(() => {
    let path = endpoint
    // Replace {id} placeholder
    if (path.includes('{id}')) {
      const id = params.id || '1'
      path = path.replace('{id}', id)
    }
    const qs = new URLSearchParams()
    for (const [k, v] of Object.entries(params)) {
      if (k === 'id' && endpoint.includes('{id}')) continue
      if (v) qs.set(k, v)
    }
    const search = qs.toString()
    return search ? `${path}?${search}` : path
  }, [endpoint, params])

  const run = async () => {
    setLoading(true)
    setResult(null)
    const url = buildUrl()
    const start = performance.now()
    try {
      const res = await fetch(url, {
        credentials: 'include',
        headers: apiKey ? { 'X-API-Key': apiKey } : {},
      })
      const elapsed = Math.round(performance.now() - start)
      const text = await res.text()
      let body: string
      try {
        body = JSON.stringify(JSON.parse(text), null, 2)
      } catch {
        body = text
      }
      setResult({ status: res.status, body, elapsed })
    } catch (err) {
      const elapsed = Math.round(performance.now() - start)
      setResult({ status: 0, body: String(err), elapsed })
    } finally {
      setLoading(false)
    }
  }

  const statusColor = result
    ? result.status >= 200 && result.status < 300
      ? 'text-success'
      : result.status >= 400
        ? 'text-destructive'
        : 'text-warning'
    : ''

  return (
    <Card className="p-4 space-y-3">
      <h3 className="text-sm font-semibold text-card-foreground">API Playground</h3>

      {/* Endpoint + API Key */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={endpoint}
          onChange={(e) => { setEndpoint(e.target.value); setParams({}); setResult(null) }}
          className="rounded-md border border-border bg-card px-2 py-1 text-sm text-foreground font-mono focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
        >
          {ENDPOINTS.map((ep) => (
            <option key={ep.value} value={ep.value}>{ep.label}</option>
          ))}
        </select>

        <input
          type="text"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder="비워두면 세션 인증 사용"
          className="rounded-md border border-border bg-card px-2 py-1 text-sm text-foreground font-mono w-full sm:w-52 focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring placeholder:text-muted-foreground"
        />

        <Button type="button" variant="primary" size="sm" onClick={run} loading={loading}>
          <Play size={12} /> Send
        </Button>
      </div>

      {/* Params */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {hints.map((key) => (
          <div key={key} className="flex items-center gap-1">
            <label className="text-[11px] text-muted-foreground font-mono shrink-0">{key}</label>
            <input
              type="text"
              value={params[key] || ''}
              onChange={(e) => setParam(key, e.target.value)}
              placeholder={key === 'limit' ? '20' : key === 'include_items' ? '0 or 1' : ''}
              className="rounded border border-border bg-card px-1.5 py-0.5 text-xs font-mono text-foreground w-full min-w-0 focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
        ))}
      </div>

      {/* Request URL preview */}
      <div className="text-[11px] text-muted-foreground font-mono truncate">
        GET {buildUrl()}
        {apiKey
          ? <span className="ml-2 text-info">+ X-API-Key</span>
          : <span className="ml-2 text-muted-foreground">session auth</span>
        }
      </div>

      {/* Result */}
      {result && (
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs">
            <span className={cn('font-mono font-semibold', statusColor)}>
              {result.status || 'ERR'}
            </span>
            <span className="text-muted-foreground tabular-nums">{result.elapsed}ms</span>
            <span className="text-muted-foreground tabular-nums">
              {(new Blob([result.body]).size / 1024).toFixed(1)}KB
            </span>
          </div>
          <pre className="bg-muted text-foreground rounded p-2 text-xs font-mono overflow-auto max-h-80 whitespace-pre">
            {result.body}
          </pre>
        </div>
      )}
    </Card>
  )
}

export default function Consumers() {
  const consumers = useConsumers()
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [showNewConsumer, setShowNewConsumer] = useState(false)
  const [newKey, setNewKey] = useState<CreateApiKeyResponse | null>(null)
  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Consumer | null>(null)
  const [rateInput, setRateInput] = useState('60')

  // Auto-select first consumer once loaded.
  useEffect(() => {
    if (selectedId == null && consumers.data && consumers.data.length > 0) {
      setSelectedId(consumers.data[0].id)
    }
  }, [consumers.data, selectedId])

  const keys = useApiKeys(selectedId)
  const createKey = useCreateApiKey(selectedId)
  const revokeKey = useRevokeApiKey(selectedId)
  const deleteKey = useDeleteApiKey(selectedId)
  const deleteConsumer = useDeleteConsumer()

  const onCreateKey = () => {
    if (selectedId == null) return
    const rate = parseInt(rateInput, 10)
    createKey.mutate(
      { rate_limit_per_min: Number.isFinite(rate) && rate > 0 ? rate : undefined },
      {
        onSuccess: (data) => setNewKey(data),
      },
    )
  }

  const selected = consumers.data?.find((c) => c.id === selectedId) ?? null

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">Consumers</h1>

      {consumers.error && <ErrorBanner error={consumers.error} />}

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {/* Left column — consumers list */}
        <Card>
          <div className="px-3 py-2 border-b border-border flex items-center justify-between">
            <span className="text-sm font-medium text-card-foreground">All consumers</span>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => setShowNewConsumer((v) => !v)}
            >
              <Plus size={12} /> New
            </Button>
          </div>
          {consumers.isLoading ? (
            <div className="p-3 text-xs text-muted-foreground">Loading…</div>
          ) : (consumers.data?.length ?? 0) === 0 ? (
            <EmptyState title="No consumers yet." />
          ) : (
            <ul>
              {consumers.data?.map((c) => (
                <li key={c.id}>
                  <ConsumerListItem
                    c={c}
                    active={c.id === selectedId}
                    onClick={() => {
                      setSelectedId(c.id)
                      setNewKey(null)
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
          {showNewConsumer && (
            <NewConsumerForm onClose={() => setShowNewConsumer(false)} />
          )}
        </Card>

        {/* Right column — selected consumer's API keys */}
        <section className="md:col-span-2 space-y-3">
          {selected ? (
            <>
              <Card className="p-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-sm text-card-foreground">{selected.slug}</div>
                  <div className="text-xs text-muted-foreground">
                    {selected.display_name} · created{' '}
                    {formatTime(selected.created_at)}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setDeleteTarget(selected)}
                  className="text-destructive-soft-foreground hover:bg-destructive-soft"
                >
                  <Trash2 size={12} /> Delete
                </Button>
              </Card>

              <Card className="p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <KeyRound size={14} className="text-muted-foreground" />
                  <span className="text-sm font-medium text-card-foreground">Issue new API key</span>
                  <Input
                    type="number"
                    min={1}
                    value={rateInput}
                    onChange={(e) => setRateInput(e.target.value)}
                    className="ml-auto w-20 tabular-nums"
                    title="Rate limit per minute"
                  />
                  <span className="text-xs text-muted-foreground">req/min</span>
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={onCreateKey}
                    loading={createKey.isPending}
                  >
                    Create
                  </Button>
                </div>
                {createKey.error && <ErrorBanner error={createKey.error} />}
              </Card>

              {newKey && (
                <NewKeyFlash result={newKey} onClose={() => setNewKey(null)} />
              )}

              {keys.error && <ErrorBanner error={keys.error} />}
              <Card className="overflow-x-auto">
                <Table>
                  <Table.Head>
                    <Table.Row>
                      <Table.Header>Preview</Table.Header>
                      <Table.Header>Limit</Table.Header>
                      <Table.Header>Usage</Table.Header>
                      <Table.Header>Last used</Table.Header>
                      <Table.Header>Created</Table.Header>
                      <Table.Header />
                    </Table.Row>
                  </Table.Head>
                  <Table.Body>
                    {keys.isLoading ? (
                      Array.from({ length: 2 }).map((_, i) => (
                        <Table.Row key={i}>
                          <Table.Cell colSpan={6}>
                            <span className="inline-block h-4 w-full rounded bg-muted animate-pulse" />
                          </Table.Cell>
                        </Table.Row>
                      ))
                    ) : (keys.data?.length ?? 0) === 0 ? (
                      <Table.Row>
                        <Table.Cell colSpan={6} className="p-0">
                          <EmptyState title="No API keys yet — issue one above." />
                        </Table.Cell>
                      </Table.Row>
                    ) : (
                      keys.data?.map((k) => (
                        <ApiKeyRow
                          key={k.id}
                          k={k}
                          onRevoke={(id, preview) =>
                            setRevokeTarget(
                              keys.data?.find((x) => x.id === id) ?? {
                                ...k,
                                preview,
                              },
                            )
                          }
                          onDelete={(id) => deleteKey.mutate(id)}
                        />
                      ))
                    )}
                  </Table.Body>
                </Table>
              </Card>

              <ApiPlayground />
            </>
          ) : (
            <Card>
              <EmptyState
                title="Select a consumer on the left"
                description={
                  <>
                    Or click <b>+ New</b> to create one.
                  </>
                }
              />
            </Card>
          )}
        </section>
      </div>

      <ConfirmDialog
        open={revokeTarget != null}
        onOpenChange={(open) => { if (!open) setRevokeTarget(null) }}
        title="Revoke API key?"
        description={
          revokeTarget && (
            <>
              <code className="font-mono text-xs">{revokeTarget.preview}…</code>{' '}
              will stop authenticating immediately. This cannot be undone.
            </>
          )
        }
        confirmVariant="destructive"
        confirmLabel="Revoke"
        loading={revokeKey.isPending}
        onConfirm={() => {
          if (!revokeTarget) return
          revokeKey.mutate(revokeTarget.id, {
            onSettled: () => setRevokeTarget(null),
          })
        }}
      />

      <ConfirmDialog
        open={deleteTarget != null}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}
        title="Delete consumer?"
        description={
          deleteTarget && (
            <>
              <code className="font-mono text-xs">{deleteTarget.slug}</code> and
              all its API keys will be permanently deleted.
            </>
          )
        }
        confirmVariant="destructive"
        confirmLabel="Delete"
        loading={deleteConsumer.isPending}
        onConfirm={() => {
          if (!deleteTarget) return
          deleteConsumer.mutate(deleteTarget.id, {
            onSettled: () => {
              setDeleteTarget(null)
              setSelectedId(null)
            },
          })
        }}
      />
    </div>
  )
}
